import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Trash2, UserPlus, Mail } from "lucide-react";

export default function Users() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", mobile: "", role: "agent" });
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/users").then((r) => setUsers(r.data));
  useEffect(() => { load(); api.get("/roles").then((r) => setRoles(r.data)).catch(() => {}); }, []);

  const roleLabel = (key) => roles.find((r) => r.key === key)?.label || (key === "admin" ? "Admin" : "NDR Agent");
  const roleIsAdmin = (key) => roles.find((r) => r.key === key)?.is_admin ?? (key === "admin");

  const create = async () => {
    if (!form.name || !form.email) { toast.error("Name and email are required"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/users", form);
      toast.success(`Invite sent to ${data.email}. A temporary password was emailed.`, { duration: 6000 });
      setOpen(false);
      setForm({ name: "", email: "", mobile: "", role: "agent" });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to add");
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this team member?")) return;
    await api.delete(`/users/${id}`);
    toast.success("Removed");
    load();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-extrabold text-[#111411] tracking-tight">Team</h1>
          <p className="text-muted-foreground mt-1">Invite NDR agents and admins. Only invited emails can access the panel (including Microsoft sign-in).</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-user-button" className="gap-2 bg-[#0B3D3D] hover:bg-[#0A2E2E] text-white">
              <UserPlus className="h-4 w-4" /> Invite member
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="user-dialog">
            <DialogHeader><DialogTitle className="font-heading">Invite team member</DialogTitle></DialogHeader>
            <p className="text-sm text-muted-foreground -mt-1 flex items-center gap-1.5">
              <Mail className="h-4 w-4" /> A temporary password will be emailed. They must reset it on first login.
            </p>
            <div className="space-y-3 py-2">
              <div><Label>Full name</Label><Input data-testid="user-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" /></div>
              <div><Label>Email ID</Label><Input type="email" data-testid="user-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" /></div>
              <div><Label>Mobile number</Label><Input data-testid="user-mobile" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} placeholder="+91…" className="mt-1.5" /></div>
              <div>
                <Label>Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="user-role" className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {roles.map((r) => <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={create} disabled={saving} data-testid="user-save" className="bg-[#0B3D3D] hover:bg-[#0A2E2E] text-white">Send invite</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="rounded-lg border border-border bg-white overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-[#F4F6F4]">
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Mobile</TableHead>
              <TableHead>Role</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.map((u) => (
              <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                <TableCell className="font-medium text-[#111411]">{u.name}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{u.email}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{u.mobile || "—"}</TableCell>
                <TableCell>
                  <Badge variant="outline" className={roleIsAdmin(u.role) ? "bg-[#E8ECE8] text-[#0B3D3D] border-[#CFDDD0]" : "bg-[#F4F6F4] text-[#5C635C]"}>
                    {roleLabel(u.role)}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  {u.id !== user?.id && (
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-[#C24127]"
                            data-testid={`delete-user-${u.id}`} onClick={() => remove(u.id)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
